/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { tokens } from "../../../../theme";
import Header from "../../../../components/Header";
import DataTable from "../../../../components/DataTable";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DataGridButton from "../../../../components/DataGridButton";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { dataGridButtonTypography } from "../../../../styles/DataGridStyles";
import { ChevronRight, Close, Sync } from "@mui/icons-material";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { DbSoftwareBuildsStyles, buildDrawerStyles } from "../../styles";
import {
  closeButtonStyles,
  dataGridButtonStyles,
} from "../../../../styles/buttonStyles";
import {
  useDeleteDbSoftwareBuild,
  useListAllDbSoftwareBuilds,
  useRetryDbSoftwareBuild,
} from "../../../../api/softwareImages";
import TimeCell from "../../../../components/TimeCell";
import { CloudIcon, EngineIcon } from "../../Utils";
import DetailPanelContent from "./DetailedPanelContent";
import useResizableDrawerWidth from "../../../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../../../components/DrawerSnapToggle";
import { useListResources } from "../../../../api/iam";
import { useNotification } from "../../../../components/Notification";
import { useConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { permissionListIncludes } from "../../../../utils/helperFunctions";
import { useReportModal } from "../../../../components/ReportModal";
import { tabbedDataGridPage } from "../../../../styles/commonPageStyles";
import { useReuploadDbPatchScriptDialog } from "../../Dialogs/ReuploadDbPatchScriptDialog";

const buildsAdapter = (builds) => {
  return builds.map((build) => {
    return {
      ...build,
      engine: build?.inputData?.engine,
      releaseBranch: build?.inputData?.releaseBranch,
      patchVersionName: build?.inputData?.patchVersionName,
      tenantName: build?.inputData?.tenantUrl?.split(".")?.[0],
    };
  });
};

const DbSoftwareBuilds = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const [builds, setBuilds] = useState([]);
  const [reload, setReload] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const isDesktopDrawer = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const {
    width: drawerWidth,
    isWide: isDrawerWide,
    toggleWide: toggleDrawerWide,
    handleProps: resizeHandleProps,
  } = useResizableDrawerWidth({
    storageKey: "db-software-build-details",
    defaultWidth: 720,
    maxWidth: 1100,
    enabled: isDesktopDrawer,
  });

  const { notificationComponent, displayNotification } = useNotification();
  const { reportModal, setReportModalOpen } = useReportModal();

  const {
    postData: getBuilds,
    response: getBuildsResp,
    isLoading: isLoadingBuilds,
  } = useListAllDbSoftwareBuilds();

  const {
    confirmationDialog: deleteDialog,
    showConfirmationDialog: showDeleteDialog,
  } = useConfirmationDialog(
    "Delete Build",
    useDeleteDbSoftwareBuild,
    displayNotification,
    getBuilds
  );

  const {
    confirmationDialog: retryDialog,
    showConfirmationDialog: showRetryDialog,
  } = useConfirmationDialog(
    "Retry Build",
    useRetryDbSoftwareBuild,
    displayNotification,
    getBuilds
  );

  const { reuploadDbPatchScriptDialog, setOpenReuploadDbPatchScriptDialog } =
    useReuploadDbPatchScriptDialog(() => getBuilds(), displayNotification);

  const deletePermissionList = useListResources(
    "/apps/tessell/software-images/db-software/builds",
    "DELETE",
    "DELETE",
    [reload]
  );

  const retryPermissionList = useListResources(
    "/apps/tessell/software-images/db-software/builds",
    "POST",
    "RETRY",
    [reload]
  );

  const reuploadPermissionList = useListResources(
    "/apps/tessell/software-images/db-software/builds",
    "PATCH",
    "REUPLOAD_SCRIPTS",
    [reload]
  );

  useEffect(() => {
    getBuilds();
  }, [reload]);

  useEffect(() => {
    setBuilds(buildsAdapter(getBuildsResp?.dbSoftwareBuilds || []));
  }, [getBuildsResp]);

  const columns = [
    {
      field: "details",
      width: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => "",
      renderCell: ({ row }) => (
        <IconButton size="small" onClick={() => setDetailsRow(row)}>
          <ChevronRight fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: "patchVersionName",
      flex: 2.5,
      cellClassName: "name-column--cell",
      renderHeader: () => "Name",
      renderCell: ({ row: { patchVersionName } }) => patchVersionName || "",
    },
    {
      field: "jiraId",
      flex: 1.5,
      renderHeader: () => "Jira Id",
      renderCell: ({ row: { jiraId } }) => jiraId || "",
    },
    {
      field: "engine",
      flex: 1,
      renderHeader: () => "Engine",
      renderCell: ({ row: { engine } }) => (
        <EngineIcon engine={engine} size="20px" />
      ),
    },
    {
      field: "cloud",
      flex: 1,
      renderHeader: () => "Cloud",
      renderCell: ({ row: { cloud } }) => (
        <CloudIcon cloud={cloud?.toLowerCase()} size="16px" />
      ),
    },
    {
      field: "releaseBranch",
      renderHeader: () => "Branch",
      flex: 1,
      renderCell: ({ row: { releaseBranch } }) => releaseBranch || "",
    },
    {
      field: "tenantName",
      renderHeader: () => "Tenant",
      flex: 2,
      renderCell: ({ row: { tenantName } }) => tenantName || "",
    },
    {
      field: "triggeredBy",
      renderHeader: () => "Triggered By",
      flex: 3,
      renderCell: ({ row: { triggeredBy } }) => triggeredBy || "",
    },
    {
      field: "dateCreated",
      renderHeader: () => "Created",
      flex: 1.3,
      minWidth: 118,
      renderCell: ({ row: { dateCreated } }) => (
        <TimeCell value={dateCreated} />
      ),
    },
    {
      field: "dateModified",
      renderHeader: () => "Modified",
      flex: 1.3,
      minWidth: 118,
      renderCell: ({ row: { dateModified } }) => (
        <TimeCell value={dateModified} />
      ),
    },
    {
      field: "actions",
      type: "actions",
      flex: 0.5,
      getActions: ({ row }) =>
        [
          permissionListIncludes(deletePermissionList, row?.id) && (
            <GridActionsCellItem
              label="Delete"
              onClick={() =>
                showDeleteDialog("Permanently delete this build data?", row?.id)
              }
              showInMenu
            />
          ),
          permissionListIncludes(reuploadPermissionList, row?.id) && (
            <GridActionsCellItem
              label="Reupload Scripts"
              onClick={() =>
                setOpenReuploadDbPatchScriptDialog(
                  row?.id,
                  row?.runData?.steps,
                  {
                    jiraId: row?.jiraId,
                    patchVersionName: row?.patchVersionName,
                    releaseBranch: row?.releaseBranch,
                  }
                )
              }
              showInMenu
            />
          ),
        ].filter(Boolean),
    },
  ];

  return (
    <Box sx={DbSoftwareBuildsStyles.mainBox}>
      {notificationComponent}
      {deleteDialog}
      {retryDialog}
      {reuploadDbPatchScriptDialog}
      {reportModal}
      <Grid sx={DbSoftwareBuildsStyles.headerGrid} container columns={2}>
        <Grid xs={1}>
          <Header
            title="DB Software Builds"
            subtitle="View and manage db software builds"
          />
        </Grid>
        <Grid xs={1}>
          <Box sx={tabbedDataGridPage.topButtonStack}>
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => setReload((prev) => !prev)}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
          </Box>
        </Grid>
      </Grid>

      <DataTable
        height="100%"
        sx={DbSoftwareBuildsStyles.dataTable}
        loading={isLoadingBuilds}
        rows={builds}
        columns={columns}
      />

      <Drawer
        anchor="right"
        open={Boolean(detailsRow)}
        onClose={() => setDetailsRow(null)}
        PaperProps={{
          sx: {
            ...buildDrawerStyles.paper(colors, drawerWidth),
            overflow: "visible",
          },
        }}
      >
        <DrawerResizeHandle
          {...resizeHandleProps}
          ariaLabel="Resize DB software build details drawer"
        />
        <DrawerSnapToggle
          enabled={isDesktopDrawer}
          isWide={isDrawerWide}
          onClick={toggleDrawerWide}
          ariaLabel="Expand DB software build details drawer to 75% width"
          collapseLabel="Collapse DB software build details drawer"
          sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
        />
        <Box sx={buildDrawerStyles.header(colors)}>
          <Box>
            <Typography sx={buildDrawerStyles.title(colors)}>
              {detailsRow?.patchVersionName}
            </Typography>
            <Typography sx={buildDrawerStyles.subtitle(colors)}>
              {detailsRow?.id}
            </Typography>
          </Box>
          <IconButton
            sx={closeButtonStyles(colors)}
            onClick={() => setDetailsRow(null)}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={buildDrawerStyles.body}>
          {detailsRow && (
            <DetailPanelContent
              row={detailsRow}
              showRetryDialog={showRetryDialog}
              retryPermissionList={retryPermissionList}
              setReportModalOpen={setReportModalOpen}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default DbSoftwareBuilds;
